import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Check, Facebook, Linkedin, Link2, MessageCircle, Share2, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { track, withUtm } from "@/lib/analytics";
import { trackShare } from "@/lib/growth.functions";

const CAMPAIGN = "listing_share";

function baseUrl(slug: string) {
  const origin =
    typeof window === "undefined" ? "https://jasasebar.com" : window.location.origin;
  return `${origin}/produk/${slug}`;
}

export function ShareListingButton({
  slug,
  name,
  rank,
  variant = "ghost",
  size = "sm",
  showLabel = true,
}: {
  slug: string;
  name: string;
  rank: number;
  variant?: "ghost" | "outline" | "default";
  size?: "sm" | "icon" | "default";
  showLabel?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const logShare = useServerFn(trackShare);

  // Anti-duplikasi sisi klien: aksi identik dalam 20 detik tidak dicatat ulang.
  const lastSent = useRef(new Map<string, number>());
  const record = (event: "share_click" | "share_copy", source: string) => {
    const key = `${event}|${source}|${slug}`;
    const now = Date.now();
    if ((lastSent.current.get(key) ?? 0) > now - 20_000) return;
    lastSent.current.set(key, now);
    track(event, { slug, source, campaign: CAMPAIGN });
    void logShare({ data: { slug, event, source, medium: "social", campaign: CAMPAIGN } }).catch(() => {});
  };

  const link = (source: string) =>
    withUtm(baseUrl(slug), { source, medium: "social", campaign: CAMPAIGN, content: slug });
  const text = (source: string) =>
    encodeURIComponent(`🔥 ${name} berada di peringkat #${rank} di jasasebar.com`) +
    `%0A${encodeURIComponent(link(source))}`;

  const shareLinks = [
    { label: "WhatsApp", source: "whatsapp", icon: MessageCircle, build: () => `https://wa.me/?text=${text("whatsapp")}` },
    {
      label: "X / Twitter",
      source: "twitter",
      icon: Twitter,
      build: () =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(`🔥 ${name} peringkat #${rank} di jasasebar.com`)}&url=${encodeURIComponent(link("twitter"))}`,
    },
    {
      label: "Facebook",
      source: "facebook",
      icon: Facebook,
      build: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link("facebook"))}`,
    },
    {
      label: "LinkedIn",
      source: "linkedin",
      icon: Linkedin,
      build: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link("linkedin"))}`,
    },
  ];

  const copy = async () => {
    await navigator.clipboard.writeText(link("copy_link"));
    record("share_copy", "copy_link");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className="gap-1.5" aria-label="Bagikan listing">
          <Share2 className="size-3.5" />
          {showLabel && <span className="hidden sm:inline">Bagikan</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {shareLinks.map((item) => (
          <DropdownMenuItem key={item.label} asChild>
            <a
              href={item.build()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => record("share_click", item.source)}
              className="flex cursor-pointer items-center gap-2"
            >
              <item.icon className="size-4" />
              <span className="text-sm">{item.label}</span>
            </a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={copy} className="flex cursor-pointer items-center gap-2">
          {copied ? <Check className="size-4 text-emerald-500" /> : <Link2 className="size-4" />}
          <span className="text-sm">{copied ? "Tersalin" : "Salin link"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
